import type { PerformanceBarLabelState, PerformanceThresholds } from '~/features/my-wallet/types/charts'
import type {
  CardProgressView,
  CardsView,
  ConditionView,
  DefaultModelMode,
  ModelCardView,
  ModelTagVariant,
  PerformanceView,
  ReceivingModelView,
  RequirementMarker,
} from '~/features/my-wallet/types/receiving-model'
import type { WalletPaymentModel } from '~/store-v2/my-wallet/useWalletPaymentModel'
import {
  KIT_TYPES,
  MODEL_CARD_STATE_PRESENTATION,
  MODEL_KEYS,
  type ModelCardState,
  type ModelKey,
  SLA_ON_TIME_DAYS,
  UNKNOWN_MODEL_CARD_STATE_PRESENTATION,
} from '~/features/my-wallet/constants/my-wallet'
import { resolveContextNote, toNextModelTarget } from '~/features/my-wallet/utils/context-note'
import { formatUpdatedAt, isWalletBlocked, resolveBestAvailableModel, resolveThresholds } from '~/features/my-wallet/utils/my-wallet'

type WalletModelCard = NonNullable<WalletPaymentModel['models']>[number]
type WalletEligibilityContext = NonNullable<WalletPaymentModel['eligibility_context']>

interface ReceivingModelContext {
  eligibilityContext: WalletEligibilityContext
  defaultModelMode: DefaultModelMode
  hasCommercialException: boolean
  isBlocked: boolean
  hasNoCompletions: boolean
}

const PHRASE_ENABLED_CARD_STATES: ModelCardState[] = ['ENABLED', 'DEFAULT']

// Only this step is flagged as alert when unmet; on a blocked card the other steps are waived.
const ALERT_REQUIREMENT_BY_CARD_STATE: Record<string, string> = {
  BLOCKED: 'REGISTRATION_ENABLED_FOR_FINANCING',
  DISABLED: 'DELINQUENCY_UNDER_CONTROL',
}

function resolveCardStatePresentation(state: string) {
  return MODEL_CARD_STATE_PRESENTATION[state as ModelCardState] ?? UNKNOWN_MODEL_CARD_STATE_PRESENTATION
}

function resolveDefaultModelMode(isEssentialProfile: boolean, isNovice: boolean): DefaultModelMode {
  if (isEssentialProfile)
    return 'essential'
  if (isNovice)
    return 'novice'
  return 'default'
}

function sortByModelKey<T extends { key: string }>(modelCards: T[]) {
  return MODEL_KEYS
    .map(modelKey => modelCards.find(modelCard => modelCard.key === modelKey))
    .filter((modelCard): modelCard is T => !!modelCard)
}

const REQUIREMENT_DISPLAY_ORDER = [
  'REGISTRATION_ENABLED_FOR_FINANCING',
  'MIN_COMPLETED_INSTALLATIONS',
  'DELINQUENCY_UNDER_CONTROL',
  'ON_TIME_PERCENTAGE_RANGE',
]

function requirementDisplayRank(code: string) {
  const rank = REQUIREMENT_DISPLAY_ORDER.indexOf(code)
  return rank === -1 ? REQUIREMENT_DISPLAY_ORDER.length : rank
}

function sortRequirements<T extends { code: string }>(requirements: T[]) {
  return [...requirements].sort((first, second) => requirementDisplayRank(first.code) - requirementDisplayRank(second.code))
}

function resolveRequirementMarker(requirement: { code: string, is_met: boolean, is_waived: boolean }, cardState: string): RequirementMarker {
  if (requirement.is_waived)
    return 'waived'

  const alertRequirementCode = ALERT_REQUIREMENT_BY_CARD_STATE[cardState]
  if (alertRequirementCode === requirement.code && !requirement.is_met)
    return 'alert'
  if (cardState === 'BLOCKED')
    return 'waived'

  return requirement.is_met ? 'done' : 'pending'
}

function resolveTagVariant(modelCard: WalletModelCard): ModelTagVariant {
  if (resolveCardStatePresentation(modelCard.state).isActive)
    return 'filled'
  return modelCard.key === 'C' && modelCard.state === 'BLOCKED' ? 'outline-strong' : 'outline-muted'
}

function buildCardProgress(modelCard: WalletModelCard, paymentModel: WalletPaymentModel, context: ReceivingModelContext): CardProgressView | null {
  const { eligibilityContext, defaultModelMode, isBlocked, hasNoCompletions } = context
  if (defaultModelMode === 'essential')
    return null

  const isNovice = defaultModelMode === 'novice'

  if (isNovice && modelCard.key === 'C') {
    return {
      kind: 'milestone',
      completed: eligibilityContext.novice_completed,
      total: eligibilityContext.novice_required,
    }
  }

  if (modelCard.state === 'WAIVED')
    return null

  const notEvaluated = isNovice || hasNoCompletions
  return {
    kind: 'performance',
    percentage: paymentModel.performance?.on_time_percentage ?? null,
    notEvaluated,
    linksToDetails: !notEvaluated && !isBlocked,
  }
}

function buildDefaultModelConditions(paymentModel: WalletPaymentModel): ConditionView[] | null {
  return paymentModel.default_model_conditions?.map(condition => ({ code: condition.code, value: condition.value ?? null })) ?? null
}

function buildModelCards(paymentModel: WalletPaymentModel, context: ReceivingModelContext): CardsView | null {
  if (!paymentModel.models)
    return null

  const { defaultModelMode } = context
  const visibleModelCards = defaultModelMode === 'essential'
    ? paymentModel.models.filter(modelCard => modelCard.key === 'C')
    : paymentModel.models

  const cardViews: ModelCardView[] = sortByModelKey(visibleModelCards).map(modelCard => ({
    key: modelCard.key as ModelKey,
    state: modelCard.state,
    isActive: resolveCardStatePresentation(modelCard.state).isActive,
    isWaived: modelCard.state === 'WAIVED',
    tagVariant: resolveTagVariant(modelCard),
    upfrontPercentage: modelCard.upfront_percentage,
    afterCompletionPercentage: modelCard.after_completion_percentage,
    conditions: modelCard.key === 'C' ? buildDefaultModelConditions(paymentModel) : [],
    requirements: sortRequirements(modelCard.requirements).map(requirement => ({
      code: requirement.code,
      marker: resolveRequirementMarker(requirement, modelCard.state),
      isWaived: requirement.is_waived,
    })),
    progress: buildCardProgress(modelCard, paymentModel, context),
  }))

  const bestAvailableModel = resolveBestAvailableModel(paymentModel.summary)
  return {
    defaultModelMode,
    items: cardViews,
    startIndex: Math.max(0, cardViews.findIndex(cardView => cardView.key === bestAvailableModel)),
  }
}

function buildBarLabelStates(modelCards: WalletModelCard[]): Partial<Record<ModelKey, PerformanceBarLabelState>> {
  const labelStates: Partial<Record<ModelKey, PerformanceBarLabelState>> = {}
  modelCards.forEach((modelCard) => {
    labelStates[modelCard.key as ModelKey] = resolveCardStatePresentation(modelCard.state).barLabelState
  })
  return labelStates
}

// Models are cumulative: reaching A also unlocks B and C. Listed from A to C, as the phrase reads.
const MODEL_KEYS_DESCENDING = [...MODEL_KEYS].reverse()

// With a commercial exception the cards no longer tell what the performance alone unlocks (they come
// waived), so this reads it from the on-time percentage and the thresholds.
function buildPerformanceModelKeys(percentage: number | null, thresholds: PerformanceThresholds): ModelKey[] {
  const minPercentageByModel: Record<ModelKey, number> = { A: thresholds.modelA, B: thresholds.modelB, C: 0 }
  return MODEL_KEYS_DESCENDING.filter(modelKey => (percentage ?? 0) >= minPercentageByModel[modelKey])
}

function buildEnabledModelKeys(
  paymentModel: WalletPaymentModel,
  context: ReceivingModelContext,
  performanceModelKeys: ModelKey[],
): ModelKey[] {
  if (context.isBlocked)
    return []

  if (context.hasCommercialException)
    return performanceModelKeys

  return MODEL_KEYS_DESCENDING
    .filter(modelKey => paymentModel.models?.some(modelCard => modelCard.key === modelKey && PHRASE_ENABLED_CARD_STATES.includes(modelCard.state as ModelCardState)))
}

function buildPerformanceView(paymentModel: WalletPaymentModel, context: ReceivingModelContext): PerformanceView | null {
  const performance = paymentModel.performance
  const thresholds = resolveThresholds(paymentModel)
  if (!performance || !thresholds)
    return null

  const { hasCommercialException, isBlocked, hasNoCompletions } = context
  const currentModel = paymentModel.summary.current_model
  const percentage = hasNoCompletions ? null : performance.on_time_percentage
  const performanceModelKeys = buildPerformanceModelKeys(percentage, thresholds)

  return {
    totalCompleted: performance.total_completed,
    onTimeCompleted: performance.on_time_completed,
    lateCompleted: performance.late_completed,
    hasNoCompletions,
    percentage,
    noCompletionsNotice: hasNoCompletions && currentModel ? { model: currentModel } : null,
    thresholds,
    enabledModels: buildEnabledModelKeys(paymentModel, context, performanceModelKeys),
    commercialModels: hasCommercialException
      ? MODEL_KEYS_DESCENDING.filter(modelKey => !performanceModelKeys.includes(modelKey))
      : [],
    contextNote: resolveContextNote({
      summary: paymentModel.summary,
      hasCommercialException,
      hasNoCompletions,
      nextModel: toNextModelTarget(performance.challenge?.target_model, performance.challenge?.projects_needed),
    }),
    labelStates: buildBarLabelStates(paymentModel.models ?? []),
    muted: isBlocked,
    commercialException: hasCommercialException,
    kits: KIT_TYPES.map((kitType) => {
      const kitPerformance = performance.by_kit_type.find(kitTypePerformance => kitTypePerformance.kit_type === kitType)
      return {
        kitType,
        onTimeCount: kitPerformance?.on_time_count ?? 0,
        lateCount: kitPerformance?.late_count ?? 0,
        slaDays: SLA_ON_TIME_DAYS[kitType],
      }
    }),
  }
}

function resolveDelinquencyBanner(summary: WalletPaymentModel['summary'], isBlocked: boolean): ReceivingModelView['delinquencyBanner'] {
  const isDisabledByDelinquency = summary.state === 'MODEL_DISABLED' && summary.disabled_reason === 'DELINQUENCY_WARNING'
  if (isBlocked || !isDisabledByDelinquency || !summary.disabled_model)
    return null
  return { model: summary.disabled_model }
}

function presentCardStates(paymentModel: WalletPaymentModel): WalletPaymentModel {
  const { models } = paymentModel
  if (!models)
    return paymentModel

  const isBlocked = isWalletBlocked(paymentModel.summary)
  return {
    ...paymentModel,
    models: models.map((modelCard) => {
      if (isBlocked)
        return { ...modelCard, state: 'BLOCKED' }
      return modelCard.key === 'C' ? { ...modelCard, state: 'DEFAULT' } : modelCard
    }),
  }
}

export function buildReceivingModelView(apiPaymentModel: WalletPaymentModel): ReceivingModelView {
  const paymentModel = presentCardStates(apiPaymentModel)
  const { summary } = paymentModel
  const eligibilityContext = paymentModel.eligibility_context
  const isEssentialProfile = summary.state === 'ESSENTIAL_PROFILE' || eligibilityContext?.partner_profile === 'essential'
  const isBlocked = isWalletBlocked(summary)
  // A block takes precedence over the commercial exception, even if the API still reports it as active.
  const hasCommercialException = eligibilityContext?.commercial_exception?.is_active === true && !isBlocked

  const context: ReceivingModelContext | null = eligibilityContext
    ? {
        eligibilityContext,
        defaultModelMode: resolveDefaultModelMode(isEssentialProfile, eligibilityContext.is_novice),
        hasCommercialException,
        isBlocked,
        hasNoCompletions: !eligibilityContext.is_novice && paymentModel.performance?.total_completed === 0,
      }
    : null

  return {
    updatedAt: formatUpdatedAt(summary.updated_at),
    isEssentialProfile,
    hasCommercialException,
    delinquencyBanner: resolveDelinquencyBanner(summary, isBlocked),
    cards: context ? buildModelCards(paymentModel, context) : null,
    novice: eligibilityContext?.is_novice
      ? {
          completed: eligibilityContext.novice_completed,
          required: eligibilityContext.novice_required,
          isDone: eligibilityContext.novice_completed >= eligibilityContext.novice_required,
        }
      : null,
    performance: context ? buildPerformanceView(paymentModel, context) : null,
  }
}
